/**
 * Posao 23-7: "firma rada" globalnog administratora (backend 23-5a, zaglavlje X-Zoma-Company).
 *
 * - Vrijedi po KARTICI preglednika (sessionStorage), nikad u localStorage: nova kartica
 *   počinje bez firme (Josipova odluka 10), dvije kartice smiju raditi u dvije firme.
 * - Postavlja je samo globalni administrator; za ostale korisnike ova datoteka ne radi ništa.
 * - Odgovor nosi X-Zoma-Acting-Company; ako se ne podudara s firmom zahtjeva, odgovor se odbacuje.
 */

export const ACTING_HEADER = 'X-Zoma-Company';
export const ACTING_RESPONSE_HEADER = 'x-zoma-acting-company';
const STORAGE_KEY = 'zoma.actingCompany';

export type ActingCompany = { id: string; name: string };

type Listener = (company: ActingCompany | null) => void;
const listeners = new Set<Listener>();
let platformAdmin = false;
let memory: ActingCompany | null = null;

function read(): ActingCompany | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return memory;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed.id === 'string' && typeof parsed.name === 'string' ? { id: parsed.id, name: parsed.name } : null;
  } catch {
    return memory;
  }
}

/** Poziva AuthContext: zaglavlje se šalje samo dok je prijavljen globalni administrator. */
export function setGlobalAdminSession(value: boolean): void {
  // Ne briše odabir: osvježavanje kartice zadržava firmu rada (sessionStorage); briše ga odjava/prijava.
  platformAdmin = value;
}

export function isGlobalAdminSession(): boolean {
  return platformAdmin;
}

export function getActingCompany(): ActingCompany | null {
  return platformAdmin ? read() : null;
}

export function setActingCompany(company: ActingCompany | null): void {
  memory = company;
  try {
    if (company) window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(company));
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch { /* sessionStorage nedostupan: vrijedi do osvježavanja kartice */ }
  listeners.forEach(listener => listener(company));
}

export function clearActingCompany({ silent = false }: { silent?: boolean } = {}): void {
  memory = null;
  try { window.sessionStorage.removeItem(STORAGE_KEY); } catch { /* nedostupan */ }
  if (!silent) listeners.forEach(listener => listener(null));
}

export function onActingCompanyChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** Zaglavlja za izravne fetch pozive (izvozi, privatne datoteke) koji zaobilaze axios. */
export function actingCompanyHeaders(): Record<string, string> {
  const company = getActingCompany();
  return company ? { [ACTING_HEADER]: company.id } : {};
}

// --- Obavijest "skriveno radi zaštite podataka" (GDPR_HIDDEN) za traku otključavanja ---
type HiddenListener = (category: string | null) => void;
const hiddenListeners = new Set<HiddenListener>();
export function notifyGdprHidden(category: string | null): void {
  hiddenListeners.forEach(listener => listener(category));
}
export function onGdprHidden(listener: HiddenListener): () => void {
  hiddenListeners.add(listener);
  return () => { hiddenListeners.delete(listener); };
}

export class ActingCompanyMismatchError extends Error {
  constructor() {
    super('Odgovor je stigao za drugu firmu od firme u kojoj radite. Osvježite stranicu.');
    this.name = 'ActingCompanyMismatchError';
  }
}
